import type {
  ApiError,
  AttemptCrimeRequest,
  AttemptCrimeResponse,
  AuthResponse,
  BailResponse,
  BustResponse,
  ClaimCourseRequest,
  ClaimCourseResponse,
  ClaimWagesResponse,
  CoursesResponse,
  CrimesIndexResponse,
  EnrollRequest,
  EnrollResponse,
  InventoryResponse,
  JoinTradeRequest,
  JoinTradeResponse,
  LoginRequest,
  MeResponse,
  RegisterRequest,
  ScholariumPerksResponse,
  ScholariumStatusResponse,
  TradesResponse,
  TrainRequest,
  TrainResponse,
  UseItemRequest,
  UseItemResponse,
  VocationStatusResponse,
  CharacterDto,
} from '@rex-mortis/shared';
import { store } from '../state/store.js';
import {
  notifyServerDown,
  notifyMaintenance,
  notifyServerOk,
} from '../components/ServerStatus.js';
import { emitTutorialSignal } from '../util/tutorialBus.js';

// Where API calls go. In dev this is `/api` (proxied by Vite to the local
// server). In production the build is given VITE_API_BASE_URL pointing at the
// Fly app (e.g. `https://api.playrexmortis.com/api`) so the static client
// hosted on Cloudflare Pages can talk to the API directly.
const ENV_BASE = (import.meta as ImportMeta & { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL;
const BASE = ENV_BASE && ENV_BASE.length > 0 ? ENV_BASE.replace(/\/$/, '') : '/api';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: ApiError['error']['details'],
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

async function parseError(res: Response): Promise<HttpError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return new HttpError(res.status, 'NETWORK', `HTTP ${res.status}`);
  }
  const e = body as ApiError | undefined;
  if (e && e.error) {
    return new HttpError(res.status, e.error.code, e.error.message, e.error.details);
  }
  return new HttpError(res.status, 'UNKNOWN', `HTTP ${res.status}`);
}

async function rawRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  withAuth = true,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = store.get().accessToken;
  if (withAuth && token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? null : JSON.stringify(body),
    });
  } catch {
    // fetch() only rejects on a true network-level failure (server unreachable,
    // DNS, offline). Surface the friendly "realm is beyond reach" takeover.
    notifyServerDown();
    throw new HttpError(0, 'NETWORK', 'Cannot reach the realm.');
  }

  // Planned maintenance — the server gates every /api route with 503 + a
  // MAINTENANCE code. Show the maintenance landing page rather than a raw error.
  if (res.status === 503) {
    const err = await parseError(res);
    if (err.code === 'MAINTENANCE') notifyMaintenance(err.message);
    throw err;
  }

  // Any successful response means the realm is reachable — clear a stale overlay.
  notifyServerOk();

  if (res.status === 204) return undefined as unknown as T;
  if (res.ok) return (await res.json()) as T;

  throw await parseError(res);
}

let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    // Hard timeout so a slow or stuck refresh can NEVER hang the boot. main()
    // awaits this before the first paint, so without a cap a sluggish startup
    // request (e.g. a server mid-redeploy, or a cold connection) leaves the
    // page blank until the boot watchdog force-reloads. Abort after 6s and
    // fall through to login instead.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        signal: ctrl.signal,
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { accessToken: string };
      store.set({ accessToken: data.accessToken });
      return data.accessToken;
    } catch (e) {
      // A genuine network failure means the realm is unreachable at startup —
      // show the down takeover. But a timeout abort just means the call was
      // slow; fall through to login so the page never hard-blanks.
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        notifyServerDown();
      }
      return null;
    } finally {
      clearTimeout(timer);
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Authenticated request with one-shot 401 → refresh → retry. */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  try {
    return await rawRequest<T>(method, path, body, true);
  } catch (err) {
    if (err instanceof HttpError && err.status === 401) {
      const newToken = await tryRefresh();
      if (newToken) {
        return rawRequest<T>(method, path, body, true);
      }
      // Refresh failed — clear session, surface error.
      store.clear();
    }
    throw err;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Typed API surface
// ──────────────────────────────────────────────────────────────────────────────

export const api = {
  async register(input: RegisterRequest): Promise<AuthResponse> {
    return rawRequest<AuthResponse>('POST', '/auth/register', input, false);
  },
  async login(input: LoginRequest): Promise<AuthResponse> {
    return rawRequest<AuthResponse>('POST', '/auth/login', input, false);
  },
  async logout(): Promise<void> {
    await request<void>('POST', '/auth/logout');
  },
  async forgotPassword(email: string): Promise<void> {
    await rawRequest<void>('POST', '/auth/forgot-password', { email }, false);
  },
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await rawRequest<void>('POST', '/auth/reset-password', { token, newPassword }, false);
  },
  async verifyEmail(token: string): Promise<{ verified: boolean; email: string }> {
    return rawRequest<{ verified: boolean; email: string }>('POST', '/auth/verify-email', { token }, false);
  },
  async resendVerification(): Promise<void> {
    await request<void>('POST', '/auth/resend-verification', {});
  },
  // Public profile (read-only scouting card for any character by id)
  async publicProfile(id: string): Promise<import('@rex-mortis/shared').PublicProfileResponse> {
    return request<import('@rex-mortis/shared').PublicProfileResponse>('GET', `/u/${encodeURIComponent(id)}`);
  },
  // Forge
  async listForgeRecipes(): Promise<import('@rex-mortis/shared').ForgeRecipesResponse> {
    return request<import('@rex-mortis/shared').ForgeRecipesResponse>('GET', '/forge/recipes');
  },
  async craft(recipeSlug: string, qty: number): Promise<import('@rex-mortis/shared').CraftResponse> {
    const r = await request<import('@rex-mortis/shared').CraftResponse>('POST', '/forge/craft', { recipeSlug, qty });
    emitTutorialSignal('craft');
    return r;
  },
  // Item Info / Circulation
  async getItemInfo(slug: string): Promise<import('@rex-mortis/shared').ItemInfoResponse> {
    return request<import('@rex-mortis/shared').ItemInfoResponse>('GET', `/items/info/${encodeURIComponent(slug)}`);
  },
  async getItemCirculation(slugs: string[]): Promise<import('@rex-mortis/shared').ItemCirculationResponse> {
    const q = slugs.map(encodeURIComponent).join(',');
    return request<import('@rex-mortis/shared').ItemCirculationResponse>('GET', `/items/circulation?slugs=${q}`);
  },
  // Replays
  async listReplays(limit?: number): Promise<import('@rex-mortis/shared').ReplayListResponse> {
    const q = typeof limit === 'number' ? `?limit=${limit}` : '';
    return request<import('@rex-mortis/shared').ReplayListResponse>('GET', `/replays/recent${q}`);
  },
  async getReplay(id: string): Promise<import('@rex-mortis/shared').ReplayDetailResponse> {
    return request<import('@rex-mortis/shared').ReplayDetailResponse>('GET', `/replays/${encodeURIComponent(id)}`);
  },
  // Titles
  async listTitles(): Promise<import('@rex-mortis/shared').TitlesResponse> {
    return request<import('@rex-mortis/shared').TitlesResponse>('GET', '/titles');
  },
  // Wiki / Compendium
  async getWikiReference(): Promise<import('@rex-mortis/shared').WikiReferenceResponse> {
    return request<import('@rex-mortis/shared').WikiReferenceResponse>('GET', '/wiki/reference');
  },
  async setActiveTitle(titleSlug: string | null): Promise<{ activeTitleSlug: string | null }> {
    return request<{ activeTitleSlug: string | null }>('POST', '/titles/active', { titleSlug });
  },
  // The Crypt (weekly co-op world boss)
  async cryptStatus(): Promise<import('@rex-mortis/shared').CryptStatusResponse> {
    return request('GET', '/crypt');
  },
  async cryptStrike(vigor?: number): Promise<import('@rex-mortis/shared').CryptStrikeResponse> {
    const r = await request<import('@rex-mortis/shared').CryptStrikeResponse>('POST', '/crypt/strike', vigor != null ? { vigor } : undefined);
    emitTutorialSignal('crypt');
    return r;
  },
  async cryptClaim(): Promise<import('@rex-mortis/shared').CryptClaimResponse> {
    return request('POST', '/crypt/claim');
  },
  // The Shade (dark NPC raid on top players)
  async darkRaidStatus(): Promise<import('@rex-mortis/shared').DarkRaidStatusResponse> {
    return request('GET', '/dark-raid');
  },
  async darkRaidConfront(): Promise<import('@rex-mortis/shared').DarkRaidConfrontResponse> {
    return request('POST', '/dark-raid/confront');
  },
  // The Shade's Due — tribute board + payment.
  async getTributeBoard(): Promise<import('@rex-mortis/shared').TributeBoardResponse> {
    return request('GET', '/dark-raid/tribute');
  },
  async payTribute(): Promise<import('@rex-mortis/shared').PayTributeResponse> {
    return request('POST', '/dark-raid/tribute');
  },
  // Individually-granted award pop-ups.
  async pendingAwards(): Promise<{ pendingAwards: import('@rex-mortis/shared').PendingAwardDto[] }> {
    return request('GET', '/player-awards');
  },
  async acceptAward(id: string): Promise<import('@rex-mortis/shared').AcceptAwardResponse> {
    return request('POST', `/player-awards/${id}/accept`);
  },
  async me(): Promise<MeResponse> {
    const res = await request<MeResponse>('GET', '/me');
    // Centralise the bank-interest + chain snapshots into the store so the
    // sidebar wallet countdown and chain tracker update on every /me poll,
    // regardless of caller.
    store.set({ bankInterest: res.bankInterest ?? null, chain: res.chain ?? null });
    return res;
  },
  async train(input: TrainRequest): Promise<TrainResponse> {
    const r = await request<TrainResponse>('POST', '/training', input);
    emitTutorialSignal('train');
    return r;
  },
  async listCrimes(): Promise<CrimesIndexResponse> {
    return request<CrimesIndexResponse>('GET', '/crimes');
  },
  async attemptCrime(input: AttemptCrimeRequest): Promise<AttemptCrimeResponse> {
    const r = await request<AttemptCrimeResponse>('POST', '/crimes/attempt', input);
    emitTutorialSignal('misdeed');
    return r;
  },
  // Good Deeds (lawful mirror of crimes)
  async listGoodDeeds(): Promise<import('@rex-mortis/shared').GoodDeedsIndexResponse> {
    return request<import('@rex-mortis/shared').GoodDeedsIndexResponse>('GET', '/good-deeds');
  },
  async attemptGoodDeed(
    input: { deedKey: string },
  ): Promise<import('@rex-mortis/shared').AttemptGoodDeedResponse> {
    const r = await request<import('@rex-mortis/shared').AttemptGoodDeedResponse>(
      'POST',
      '/good-deeds/attempt',
      input,
    );
    emitTutorialSignal('goodDeed');
    return r;
  },
  // Grand Schemes / Great Works — multi-stage endgame chains.
  async listChains(): Promise<import('@rex-mortis/shared').ChainsIndexResponse> {
    return request<import('@rex-mortis/shared').ChainsIndexResponse>('GET', '/chains');
  },
  async attemptChain(
    input: import('@rex-mortis/shared').AttemptChainRequest,
  ): Promise<import('@rex-mortis/shared').AttemptChainResponse> {
    return request<import('@rex-mortis/shared').AttemptChainResponse>(
      'POST',
      '/chains/attempt',
      input,
    );
  },
  async listInventory(): Promise<InventoryResponse> {
    const r = await request<InventoryResponse>('GET', '/inventory');
    emitTutorialSignal('inventory');
    return r;
  },
  async useItem(input: UseItemRequest): Promise<UseItemResponse> {
    return request<UseItemResponse>('POST', '/items/use', input);
  },
  async bustDungeon(): Promise<BustResponse> {
    return request<BustResponse>('POST', '/dungeon/bust');
  },
  async bailDungeon(): Promise<BailResponse> {
    return request<BailResponse>('POST', '/dungeon/bail');
  },
  // Scholarium
  async listCourses(): Promise<CoursesResponse> {
    return request<CoursesResponse>('GET', '/scholarium');
  },
  async activeEnrollment(): Promise<ScholariumStatusResponse> {
    return request<ScholariumStatusResponse>('GET', '/scholarium/active');
  },
  async enrollInCourse(input: EnrollRequest): Promise<EnrollResponse> {
    return request<EnrollResponse>('POST', '/scholarium/enroll', input);
  },
  async claimCourse(input: ClaimCourseRequest): Promise<ClaimCourseResponse> {
    return request<ClaimCourseResponse>('POST', '/scholarium/claim', input);
  },
  async getPerks(): Promise<ScholariumPerksResponse> {
    return request<ScholariumPerksResponse>('GET', '/scholarium/perks');
  },
  // Vocations
  async listTrades(): Promise<TradesResponse> {
    return request<TradesResponse>('GET', '/vocations');
  },
  async activeVocation(): Promise<VocationStatusResponse> {
    return request<VocationStatusResponse>('GET', '/vocations/active');
  },
  async joinTrade(input: JoinTradeRequest): Promise<JoinTradeResponse> {
    const r = await request<JoinTradeResponse>('POST', '/vocations/join', input);
    emitTutorialSignal('trade');
    return r;
  },
  async claimWages(): Promise<ClaimWagesResponse> {
    const r = await request<ClaimWagesResponse>('POST', '/vocations/claim');
    emitTutorialSignal('trade');
    return r;
  },
  async leaveTrade(): Promise<{ character: CharacterDto }> {
    return request<{ character: CharacterDto }>('POST', '/vocations/leave');
  },
  // Market
  async searchMarket(params: {
    query?: string | undefined;
    category?: string | undefined;
    maxPricePer?: string | undefined;
    limit?: number | undefined;
  } = {}): Promise<import('@rex-mortis/shared').MarketSearchResponse> {
    const q = new URLSearchParams();
    if (params.query) q.set('query', params.query);
    if (params.category) q.set('category', params.category);
    if (params.maxPricePer) q.set('maxPricePer', params.maxPricePer);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return request('GET', `/market${qs ? `?${qs}` : ''}`);
  },
  async listOwnMarket(): Promise<import('@rex-mortis/shared').MyListingsResponse> {
    return request('GET', '/market/mine');
  },
  async listOnMarket(input: import('@rex-mortis/shared').ListItemRequest): Promise<import('@rex-mortis/shared').ListItemResponse> {
    return request('POST', '/market/list', input);
  },
  async buyOnMarket(input: import('@rex-mortis/shared').BuyListingRequest): Promise<import('@rex-mortis/shared').BuyListingResponse> {
    return request('POST', '/market/buy', input);
  },
  async cancelMarketListing(input: import('@rex-mortis/shared').CancelListingRequest): Promise<import('@rex-mortis/shared').CancelListingResponse> {
    return request('POST', '/market/cancel', input);
  },
  // Travel & Ships
  async travelStatus(): Promise<import('@rex-mortis/shared').TravelStatusResponse> {
    return request('GET', '/travel/status');
  },
  async depart(input: import('@rex-mortis/shared').DepartRequest): Promise<import('@rex-mortis/shared').DepartResponse> {
    const r = await request<import('@rex-mortis/shared').DepartResponse>('POST', '/travel/depart', input);
    emitTutorialSignal('travel');
    return r;
  },
  async buyShop(input: import('@rex-mortis/shared').BuyShopRequest): Promise<import('@rex-mortis/shared').BuyShopResponse> {
    return request('POST', '/travel/shop/buy', input);
  },
  async listShips(): Promise<import('@rex-mortis/shared').ShipsResponse> {
    return request('GET', '/travel/shipyard');
  },
  async buyShip(input: import('@rex-mortis/shared').BuyShipRequest): Promise<import('@rex-mortis/shared').BuyShipResponse> {
    return request('POST', '/travel/shipyard/buy', input);
  },
  async switchShip(input: import('@rex-mortis/shared').SwitchShipRequest): Promise<import('@rex-mortis/shared').SwitchShipResponse> {
    return request('POST', '/travel/shipyard/switch', input);
  },
  // Bank / Counting House
  async getBank(): Promise<import('@rex-mortis/shared').BankStateResponse> {
    return request('GET', '/banks/mine');
  },
  async depositBank(input: import('@rex-mortis/shared').BankDepositRequest): Promise<import('@rex-mortis/shared').BankStateResponse> {
    const r = await request<import('@rex-mortis/shared').BankStateResponse>('POST', '/banks/deposit', input);
    emitTutorialSignal('bank');
    return r;
  },
  async withdrawBank(input: import('@rex-mortis/shared').BankWithdrawRequest): Promise<import('@rex-mortis/shared').BankStateResponse> {
    return request('POST', '/banks/withdraw', input);
  },
  async upgradeBank(input: import('@rex-mortis/shared').BankUpgradeRequest): Promise<import('@rex-mortis/shared').BankStateResponse> {
    return request('POST', '/banks/upgrade', input);
  },
  // Mail
  async listMail(opts: { folder?: 'inbox' | 'sent'; unread?: boolean; limit?: number; offset?: number } = {}): Promise<import('@rex-mortis/shared').MailListResponse> {
    const q = new URLSearchParams();
    if (opts.folder) q.set('folder', opts.folder);
    if (opts.unread) q.set('unread', '1');
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return request('GET', `/mail${qs ? `?${qs}` : ''}`);
  },
  async getMail(id: string): Promise<import('@rex-mortis/shared').MailDetailResponse> {
    return request('GET', `/mail/${encodeURIComponent(id)}`);
  },
  async sendMail(input: import('@rex-mortis/shared').SendMailRequest): Promise<import('@rex-mortis/shared').SendMailResponse> {
    return request('POST', '/mail', input);
  },
  async deleteMail(id: string): Promise<void> {
    return request('DELETE', `/mail/${encodeURIComponent(id)}`);
  },
  async mailUnreadCount(): Promise<import('@rex-mortis/shared').MailUnreadCountResponse> {
    return request('GET', '/mail/unread-count');
  },
  // Notifications
  async listNotifications(opts: { unread?: boolean; limit?: number; offset?: number } = {}): Promise<import('@rex-mortis/shared').NotificationListResponse> {
    const q = new URLSearchParams();
    if (opts.unread) q.set('unread', '1');
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return request('GET', `/notifications${qs ? `?${qs}` : ''}`);
  },
  async notificationsUnreadCount(): Promise<import('@rex-mortis/shared').NotificationUnreadCountResponse> {
    return request('GET', '/notifications/unread-count');
  },
  async markNotificationRead(id: string): Promise<void> {
    return request('POST', `/notifications/${encodeURIComponent(id)}/read`);
  },
  async markAllNotificationsRead(): Promise<{ markedCount: number }> {
    return request('POST', '/notifications/read-all');
  },
  /** Clear unread house-chat bell notifications — called when the House chat is opened. */
  async markHouseChatNotificationsRead(): Promise<{ markedCount: number }> {
    return request('POST', '/notifications/house-chat-read');
  },
  // Settings
  async changePassword(input: import('@rex-mortis/shared').ChangePasswordRequest): Promise<void> {
    return request('POST', '/auth/change-password', input);
  },
  // Leaderboards
  async leaderboardMetrics(): Promise<import('@rex-mortis/shared').LeaderboardMetricsResponse> {
    return request('GET', '/leaderboards/metrics');
  },
  async getLeaderboard(type: 'players' | 'houses', metric: string, opts: { limit?: number; offset?: number } = {}): Promise<import('@rex-mortis/shared').LeaderboardResponse> {
    const q = new URLSearchParams();
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return request('GET', `/leaderboards/${type}/${encodeURIComponent(metric)}${qs ? `?${qs}` : ''}`);
  },
  // Career stats
  async getCareerStats(): Promise<import('@rex-mortis/shared').CareerStatsResponse> {
    return request('GET', '/me/stats');
  },
  async getActivity(limit = 30): Promise<import('@rex-mortis/shared').ActivityResponse> {
    return request('GET', `/me/activity?limit=${limit}`);
  },
  // Friends + Blocks
  async listFriends(): Promise<import('@rex-mortis/shared').FriendsListResponse> {
    return request('GET', '/friends');
  },
  async listBlocks(): Promise<import('@rex-mortis/shared').BlocksListResponse> {
    return request('GET', '/friends/blocked');
  },
  async addFriend(input: import('@rex-mortis/shared').AddFriendRequest): Promise<import('@rex-mortis/shared').AddFriendResponse> {
    return request('POST', '/friends', input);
  },
  async removeFriend(id: string): Promise<void> {
    return request('DELETE', `/friends/${encodeURIComponent(id)}`);
  },
  async addBlock(input: import('@rex-mortis/shared').AddFriendRequest): Promise<import('@rex-mortis/shared').AddBlockResponse> {
    return request('POST', '/friends/blocked', input);
  },
  async removeBlock(id: string): Promise<void> {
    return request('DELETE', `/friends/blocked/${encodeURIComponent(id)}`);
  },
  async listEnemies(): Promise<import('@rex-mortis/shared').EnemiesListResponse> {
    return request('GET', '/friends/enemies');
  },
  async addEnemy(input: import('@rex-mortis/shared').AddFriendRequest): Promise<import('@rex-mortis/shared').AddEnemyResponse> {
    return request('POST', '/friends/enemies', input);
  },
  async removeEnemy(id: string): Promise<void> {
    return request('DELETE', `/friends/enemies/${encodeURIComponent(id)}`);
  },
  // Daily quests
  async getTodayQuests(): Promise<import('@rex-mortis/shared').TodayQuestsResponse> {
    return request('GET', '/me/quests');
  },
  async claimQuest(slug: string): Promise<import('@rex-mortis/shared').ClaimQuestResponse> {
    return request('POST', `/me/quests/${encodeURIComponent(slug)}/claim`);
  },
  // Almsgiving (curio donations)
  async getTodayDonations(): Promise<import('@rex-mortis/shared').TodayDonationsResponse> {
    return request('GET', '/me/donations');
  },
  async fulfilDonation(itemSlug: string): Promise<import('@rex-mortis/shared').FulfilDonationResponse> {
    return request('POST', `/me/donations/${encodeURIComponent(itemSlug)}/fulfil`);
  },
  // The Wandering Caravan (crafting commissions + Seal shop)
  async getCaravan(): Promise<import('@rex-mortis/shared').CaravanIndexResponse> {
    return request('GET', '/caravan');
  },
  async fulfilCommission(family: string): Promise<import('@rex-mortis/shared').FulfilCommissionResponse> {
    return request('POST', `/caravan/commissions/${encodeURIComponent(family)}/fulfil`);
  },
  async buyCaravanWare(wareSlug: string): Promise<import('@rex-mortis/shared').BuyWareResponse> {
    return request('POST', '/caravan/buy', { wareSlug });
  },
  // Gathering (Fishing)
  async getGatheringState(): Promise<import('@rex-mortis/shared').GatheringStateResponse> {
    return request('GET', '/gathering/state');
  },
  async gather(skill: string, vigor: number): Promise<import('@rex-mortis/shared').GatherResponse> {
    const r = await request<import('@rex-mortis/shared').GatherResponse>('POST', `/gathering/${encodeURIComponent(skill)}`, { vigor });
    emitTutorialSignal('gather');
    return r;
  },
  async getGatheringSkills(): Promise<import('@rex-mortis/shared').GatheringSkillsResponse> {
    return request('GET', '/gathering/skills');
  },
  // Domiciles
  async listDomiciles(): Promise<import('@rex-mortis/shared').DomicileCatalogueResponse> {
    return request('GET', '/domiciles');
  },
  async myDomicile(): Promise<import('@rex-mortis/shared').MyDomicileResponse> {
    return request('GET', '/domiciles/mine');
  },
  async buyDomicile(input: import('@rex-mortis/shared').BuyDomicileRequest): Promise<import('@rex-mortis/shared').BuyDomicileResponse> {
    return request('POST', '/domiciles/buy', input);
  },
  async sellDomicile(): Promise<import('@rex-mortis/shared').SellDomicileResponse> {
    return request('POST', '/domiciles/sell');
  },
  async installDomicileUpgrade(input: import('@rex-mortis/shared').UpgradeRequest): Promise<import('@rex-mortis/shared').UpgradeResponse> {
    return request('POST', '/domiciles/upgrade', input);
  },
  async uninstallDomicileUpgrade(input: import('@rex-mortis/shared').UpgradeRequest): Promise<import('@rex-mortis/shared').UninstallUpgradeResponse> {
    return request('POST', '/domiciles/uninstall', input);
  },
  async installDomicileFurnishing(input: import('@rex-mortis/shared').FurnishingRequest): Promise<import('@rex-mortis/shared').FurnishingResponse> {
    return request('POST', '/domiciles/furnish', input);
  },
  async removeDomicileFurnishing(input: import('@rex-mortis/shared').FurnishingRequest): Promise<import('@rex-mortis/shared').FurnishingResponse> {
    return request('POST', '/domiciles/unfurnish', input);
  },
  // Personal Vault (domicile-backed crown storage)
  async getVault(): Promise<{
    unlocked: boolean;
    unlockedBy: string | null;
    vaultCrowns: string;
    walletCrowns: string;
  }> {
    return request('GET', '/vault');
  },
  async vaultDeposit(amount: string): Promise<{
    character: import('@rex-mortis/shared').CharacterDto;
    vault: {
      unlocked: boolean;
      unlockedBy: string | null;
      vaultCrowns: string;
      walletCrowns: string;
    };
    moved: string;
  }> {
    return request('POST', '/vault/deposit', { amount });
  },
  async getDomicileUpkeep(): Promise<{
    perWeek: string;
    weeksPending: number;
    amountPending: string;
    evictionThreshold: string;
    currentDebt: string;
  }> {
    return request('GET', '/domiciles/upkeep');
  },
  async payDomicileDebt(amount: string): Promise<{
    paid: string;
    remainingDebt: string;
    character: import('@rex-mortis/shared').CharacterDto;
  }> {
    return request('POST', '/domiciles/pay-debt', { amount });
  },
  async vaultWithdraw(amount: string): Promise<{
    character: import('@rex-mortis/shared').CharacterDto;
    vault: {
      unlocked: boolean;
      unlockedBy: string | null;
      vaultCrowns: string;
      walletCrowns: string;
    };
    moved: string;
  }> {
    return request('POST', '/vault/withdraw', { amount });
  },
  // Combat
  async listDummies(): Promise<import('@rex-mortis/shared').NpcsResponse> {
    return request('GET', '/combat/dummies');
  },
  async getLoadout(): Promise<import('@rex-mortis/shared').LoadoutResponse> {
    return request('GET', '/combat/loadout');
  },
  async getNpcLoot(slug: string): Promise<import('@rex-mortis/shared').NpcLootResponse> {
    return request('GET', `/combat/dummies/${encodeURIComponent(slug)}/loot`);
  },
  async activityStatus(): Promise<import('@rex-mortis/shared').ActivityStatusResponse> {
    return request('GET', '/activity-status');
  },
  async equip(input: import('@rex-mortis/shared').EquipRequest): Promise<import('@rex-mortis/shared').EquipResponse> {
    return request('POST', '/combat/equip', input);
  },
  async unequip(input: import('@rex-mortis/shared').UnequipRequest): Promise<import('@rex-mortis/shared').UnequipResponse> {
    return request('POST', '/combat/unequip', input);
  },
  async attackNpc(input: import('@rex-mortis/shared').AttackNpcRequest): Promise<import('@rex-mortis/shared').AttackNpcResponse> {
    const r = await request<import('@rex-mortis/shared').AttackNpcResponse>('POST', '/combat/attack-npc', input);
    emitTutorialSignal('spar');
    return r;
  },
  // PvP
  async findTarget(input: import('@rex-mortis/shared').FindTargetRequest): Promise<import('@rex-mortis/shared').FindTargetResponse> {
    return request('POST', '/attacks/find', input);
  },
  async attackPlayer(input: import('@rex-mortis/shared').AttackPlayerRequest): Promise<import('@rex-mortis/shared').AttackPlayerResponse> {
    return request('POST', '/attacks/attack-player', input);
  },
  async playersHere(): Promise<import('@rex-mortis/shared').PlayersHereResponse> {
    return request('GET', '/attacks/here');
  },
  async suggestTargets(q: string): Promise<import('@rex-mortis/shared').TargetSuggestionsResponse> {
    return request('GET', `/attacks/suggest?q=${encodeURIComponent(q)}`);
  },
  // Houses
  async listHouses(): Promise<import('@rex-mortis/shared').HousesIndexResponse> {
    return request('GET', '/houses');
  },
  async myHouse(): Promise<import('@rex-mortis/shared').MyHouseResponse> {
    return request('GET', '/houses/mine');
  },
  async createHouse(input: import('@rex-mortis/shared').CreateHouseRequest): Promise<import('@rex-mortis/shared').CreateHouseResponse> {
    const r = await request<import('@rex-mortis/shared').CreateHouseResponse>('POST', '/houses', input);
    emitTutorialSignal('house');
    return r;
  },
  async applyToHouse(input: import('@rex-mortis/shared').ApplyHouseRequest): Promise<import('@rex-mortis/shared').ApplyHouseResponse> {
    const r = await request<import('@rex-mortis/shared').ApplyHouseResponse>('POST', '/houses/apply', input);
    emitTutorialSignal('house');
    return r;
  },
  async cancelHouseApplication(): Promise<{ ok: boolean }> {
    return request('DELETE', '/houses/application');
  },
  async listHouseApplications(): Promise<import('@rex-mortis/shared').HouseApplicationsResponse> {
    return request('GET', '/houses/applications');
  },
  async reviewHouseApplication(input: import('@rex-mortis/shared').ReviewApplicationRequest): Promise<import('@rex-mortis/shared').HouseDetailDto> {
    return request('POST', '/houses/applications/review', input);
  },
  async publicHouse(id: string): Promise<import('@rex-mortis/shared').PublicHouseResponse> {
    return request('GET', `/houses/public/${encodeURIComponent(id)}`);
  },
  async setHouseBanner(input: import('@rex-mortis/shared').SetHouseBannerRequest): Promise<import('@rex-mortis/shared').HouseDetailDto> {
    return request('POST', '/houses/banner', input);
  },
  async clearHouseBanner(): Promise<import('@rex-mortis/shared').HouseDetailDto> {
    return request('DELETE', '/houses/banner');
  },
  async leaveHouse(): Promise<{ character: CharacterDto }> {
    return request('POST', '/houses/leave');
  },
  async depositTreasury(input: import('@rex-mortis/shared').DepositTreasuryRequest): Promise<import('@rex-mortis/shared').DepositTreasuryResponse> {
    return request('POST', '/houses/deposit', input);
  },
  async setHouseRank(input: import('@rex-mortis/shared').SetRankRequest): Promise<import('@rex-mortis/shared').HouseDetailDto> {
    return request('POST', '/houses/rank', input);
  },
  async kickFromHouse(input: import('@rex-mortis/shared').KickRequest): Promise<import('@rex-mortis/shared').HouseDetailDto> {
    return request('POST', '/houses/kick', input);
  },
  // House perk tree
  async listHousePerks(): Promise<import('@rex-mortis/shared').HousePerksResponse> {
    return request('GET', '/houses/perks');
  },
  async buyHousePerk(input: import('@rex-mortis/shared').BuyHousePerkRequest): Promise<import('@rex-mortis/shared').BuyHousePerkResponse> {
    return request('POST', '/houses/perks/buy', input);
  },
  // Wars
  async listWars(): Promise<import('@rex-mortis/shared').WarsResponse> {
    return request('GET', '/houses/wars');
  },
  async declareWar(input: import('@rex-mortis/shared').DeclareWarRequest): Promise<import('@rex-mortis/shared').WarDto> {
    return request('POST', '/houses/wars/declare', input);
  },
  // Holdings
  async listHoldings(): Promise<import('@rex-mortis/shared').HoldingsResponse> {
    return request('GET', '/houses/holdings');
  },
  async claimHolding(input: import('@rex-mortis/shared').ClaimHoldingRequest): Promise<import('@rex-mortis/shared').HoldingsResponse> {
    return request('POST', '/houses/holdings/claim', input);
  },
  async applyHoldingPayouts(): Promise<import('@rex-mortis/shared').HoldingsResponse> {
    return request('POST', '/houses/holdings/payout');
  },
  // Awards
  async listAwards(): Promise<import('@rex-mortis/shared').AwardsResponse> {
    return request('GET', '/awards');
  },
  async listBoons(): Promise<import('@rex-mortis/shared').BoonsResponse> {
    return request('GET', '/awards/boons');
  },
  async buyBoon(input: import('@rex-mortis/shared').BuyBoonRequest): Promise<import('@rex-mortis/shared').BuyBoonResponse> {
    return request('POST', '/awards/boons/buy', input);
  },
  // Training tiers
  async listTrainingTiers(): Promise<import('@rex-mortis/shared').TrainingTiersResponse> {
    return request('GET', '/training/tiers');
  },
  // Expeditions
  async listExpeditions(): Promise<import('@rex-mortis/shared').ExpeditionsResponse> {
    return request('GET', '/expeditions');
  },
  async startExpedition(input: import('@rex-mortis/shared').StartExpeditionRequest): Promise<import('@rex-mortis/shared').StartExpeditionResponse> {
    return request('POST', '/expeditions/start', input);
  },
  async claimExpedition(): Promise<import('@rex-mortis/shared').ClaimExpeditionResponse> {
    return request('POST', '/expeditions/claim');
  },
  async abandonExpedition(): Promise<{ character: CharacterDto }> {
    return request('POST', '/expeditions/abandon');
  },
  // Events
  async listEvents(): Promise<{ events: Array<{ id: string; slug: string; name: string; blurb: string; category: string; startsAt: string; endsAt: string; active: boolean; titleReward: string; crownReward: number; boonReward: number; claimed: boolean }> }> {
    return request('GET', '/events');
  },
  async claimEvent(instanceId: string): Promise<{ instanceId: string; eventSlug: string; titleAwarded: string; crownsPaid: number; boonsPaid: number }> {
    return request('POST', '/events/claim', { instanceId });
  },
  // Chat / Shoutbox
  async listShouts(channel: string, limit = 50): Promise<{ messages: Array<{ id: string; characterId: string; characterName: string; titleSlug: string | null; channel: string; body: string; createdAt: string }> }> {
    const q = new URLSearchParams({ channel, limit: String(limit) });
    return request('GET', `/chat?${q.toString()}`);
  },
  async postShout(channel: string, body: string): Promise<{ message: { id: string; characterId: string; characterName: string; titleSlug: string | null; channel: string; body: string; createdAt: string } }> {
    const r = await request<{ message: { id: string; characterId: string; characterName: string; titleSlug: string | null; channel: string; body: string; createdAt: string } }>('POST', '/chat', { channel, body });
    emitTutorialSignal('shout');
    return r;
  },
  /** Souls seen in the realm in the last 15 minutes (sidebar presence panel). */
  async recentlyActive(): Promise<{ total: number; active: Array<{ characterId: string; name: string; level: number; activeTitleSlug: string | null; minutesAgo: number }> }> {
    return request('GET', '/presence/recent');
  },
  /** Activity heartbeat — marks the player genuinely present. Sent only while the
   *  tab is visible and the player has interacted recently (see presenceHeartbeat). */
  async heartbeat(): Promise<void> {
    await request('POST', '/presence/heartbeat');
  },
  /** The Compline bell's current state (active blessing, next toll, window end). */
  async complineStatus(): Promise<{
    active: { key: string; label: string; blurb: string; effect: string; bonusPct: number } | null;
    nextTollAt: string;
    endsAt: string | null;
    tollHour: number;
    timeZone: string;
  }> {
    return request('GET', '/compline/status');
  },
  // Patch notes
  async listPatchNotes(): Promise<{ notes: Array<{ id: string; version: string; title: string; body: string; publishedAt: string }> }> {
    return request('GET', '/patch-notes');
  },
  // Pinned awards
  async setPinnedAwards(slugs: string[]): Promise<{ pinnedAwardSlugs: string[] }> {
    return request('POST', '/me/pinned-awards', { slugs });
  },
  // Tutorial
  async setTutorialStep(step: number): Promise<{ tutorialStep: number }> {
    return request('POST', '/me/tutorial-step', { step });
  },
  // NPC merchant
  async getMerchantBids(): Promise<{ bids: Array<{ slug: string; name: string; tier: string; bid: string }> }> {
    return request('GET', '/market/merchant/bids');
  },
  async sellToMerchant(itemSlug: string, qty: number): Promise<{ character: CharacterDto; itemSlug: string; itemName: string; qtySold: number; pricePerItem: string; totalPaid: string }> {
    return request('POST', '/market/merchant/sell', { itemSlug, qty });
  },
  // Feedback / bug reports
  async submitFeedback(input: {
    category: 'bug' | 'idea' | 'balance' | 'other';
    subject: string;
    body: string;
    contextUrl?: string;
  }): Promise<{ ok: boolean }> {
    return request('POST', '/feedback', input);
  },
  // Rules / TOS acceptance — fired by the re-acceptance modal when the
  // server reports termsCurrent=false on the /me payload.
  async acceptTerms(): Promise<{ termsCurrent: boolean; termsVersion: string }> {
    return request('POST', '/me/accept-terms');
  },

  // Membership + Coin Shop
  async getMembership(): Promise<{
    current: import('@rex-mortis/shared').MembershipStatusDto;
    tiers: import('@rex-mortis/shared').MembershipPerksDto[];
    chests: import('@rex-mortis/shared').ChestShopItemDto[];
  }> {
    return request('GET', '/membership');
  },
  async createPaymentOrder(
    input: import('@rex-mortis/shared').CreateOrderRequest,
  ): Promise<import('@rex-mortis/shared').CreateOrderResponse> {
    return request('POST', '/payments/order', input);
  },
  async capturePayment(
    paypalOrderId: string,
  ): Promise<import('@rex-mortis/shared').CapturePaymentResponse> {
    return request('POST', '/payments/capture', { paypalOrderId });
  },
  async devApprovePayment(
    paypalOrderId: string,
  ): Promise<import('@rex-mortis/shared').CapturePaymentResponse> {
    return request('POST', '/payments/dev-approve', { paypalOrderId });
  },

  // Rescue — freeing other players from the Dungeon / Infirmary
  async listDungeonOccupants(): Promise<import('@rex-mortis/shared').OccupantsResponse> {
    return request('GET', '/rescue/dungeon');
  },
  async listInfirmaryOccupants(): Promise<import('@rex-mortis/shared').OccupantsResponse> {
    return request('GET', '/rescue/infirmary');
  },
  async breakoutPrisoner(
    targetCharacterId: string,
  ): Promise<import('@rex-mortis/shared').BreakoutResponse> {
    return request('POST', '/rescue/breakout', { targetCharacterId });
  },
  async revivePatient(
    targetCharacterId: string,
  ): Promise<import('@rex-mortis/shared').ReviveResponse> {
    return request('POST', '/rescue/revive', { targetCharacterId });
  },
  // Send crowns (peer-to-peer wallet gift)
  async sendCrowns(
    input: import('@rex-mortis/shared').SendCrownsRequest,
  ): Promise<import('@rex-mortis/shared').SendCrownsResponse> {
    return request('POST', '/transfer/crowns', input);
  },

  // Refill tokens
  async getRefillStatus(): Promise<import('@rex-mortis/shared').RefillStatusResponse> {
    return request('GET', '/refill');
  },
  async useRefill(
    bar: import('@rex-mortis/shared').RefillableBar,
  ): Promise<import('@rex-mortis/shared').UseRefillResponse> {
    return request('POST', '/refill/use', { bar });
  },

  // Avatar library
  async getAvatarLibrary(): Promise<import('@rex-mortis/shared').AvatarLibraryResponse> {
    return request('GET', '/avatars');
  },
  async uploadAvatar(
    dataUrl: string,
  ): Promise<import('@rex-mortis/shared').AvatarMutationResponse> {
    return request('POST', '/avatars/upload', { dataUrl });
  },
  async selectAvatar(
    url: string | null,
  ): Promise<import('@rex-mortis/shared').AvatarMutationResponse> {
    return request('POST', '/avatars/select', { url });
  },
  async deleteAvatar(
    url: string,
  ): Promise<import('@rex-mortis/shared').AvatarMutationResponse> {
    return request('DELETE', '/avatars', { url });
  },
  // Horse Racing — the Racing Yard
  async getRacingYard(): Promise<import('@rex-mortis/shared').RacingYardResponse> {
    return request('GET', '/racing');
  },
  async getRacingRankings(): Promise<import('@rex-mortis/shared').RacingRankingsResponse> {
    return request('GET', '/racing/rankings');
  },
  async buyHorse(horseSlug: string): Promise<import('@rex-mortis/shared').BuyHorseResponse> {
    return request('POST', '/racing/horse/buy', { horseSlug });
  },
  async renameHorse(horseId: string, name: string): Promise<import('@rex-mortis/shared').StableMutationResponse> {
    return request('POST', '/racing/horse/rename', { horseId, name });
  },
  async fitHorseGear(horseId: string, gearSlug: string): Promise<import('@rex-mortis/shared').StableMutationResponse> {
    return request('POST', '/racing/horse/fit', { horseId, gearSlug });
  },
  async buyStableSlot(): Promise<import('@rex-mortis/shared').StableMutationResponse> {
    return request('POST', '/racing/stable/slot');
  },
  async enterRace(horseId: string, trackSlug: string): Promise<import('@rex-mortis/shared').EnterRaceResponse> {
    const r = await request<import('@rex-mortis/shared').EnterRaceResponse>('POST', '/racing/race/enter', { horseId, trackSlug });
    emitTutorialSignal('race');
    return r;
  },
  async claimRace(entryId: string): Promise<import('@rex-mortis/shared').ClaimRaceResponse> {
    return request('POST', '/racing/race/claim', { entryId });
  },
  async sellHorseToMerchant(horseId: string): Promise<import('@rex-mortis/shared').SellHorseMerchantResponse> {
    return request('POST', '/racing/horse/sell-merchant', { horseId });
  },
  async listHorse(horseId: string, price: string): Promise<import('@rex-mortis/shared').StableMutationResponse> {
    return request('POST', '/racing/horse/list', { horseId, price });
  },
  async unlistHorse(horseId: string): Promise<import('@rex-mortis/shared').StableMutationResponse> {
    return request('POST', '/racing/horse/unlist', { horseId });
  },
  async getHorseMarket(): Promise<import('@rex-mortis/shared').HorseMarketResponse> {
    return request('GET', '/racing/market');
  },
  async buyHorseListing(listingId: string): Promise<import('@rex-mortis/shared').BuyHorseListingResponse> {
    return request('POST', '/racing/market/buy', { listingId });
  },

  /** Probe on startup: try to refresh, then load /me. Returns null if not signed in. */
  async bootstrapSession(): Promise<MeResponse | null> {
    const token = await tryRefresh();
    if (!token) return null;
    try {
      return await api.me();
    } catch {
      return null;
    }
  },
};
